<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Una foto 360° es una imagen esférica que captura simultáneamente una
            vista horizontal completa de 360° y una vista vertical de 180°,
            creando un registro fotográfico de cada dirección desde un único
            punto en el espacio. A diferencia de una fotografía estándar que
            captura una franja rectangular de tu campo visual, la foto 360°
            captura el entorno completo alrededor del fotógrafo. Al verla puedes
            girar la vista, hacer zoom y mirar en cualquier dirección, incluidas
            directamente hacia arriba y hacia abajo.
          </p>
        </section>

        <section>
          <h2>Cómo se crea una foto 360°</h2>

          <h3>El proceso técnico</h3>
          <p>
            Las fotos 360° se crean mediante un proceso llamado
            <strong>costura de imágenes</strong> (stitching). Varias fotografías
            superpuestas tomadas en distintas direcciones desde el mismo punto
            se alinean matemáticamente y se fusionan para crear una esfera
            homogénea. El archivo resultante se almacena en formato
            equirectangular: un archivo plano y rectangular (relación 2:1) que
            representa la superficie esférica proyectada en un plano, como un
            mapa del mundo representa la Tierra esférica en una página.
          </p>
          <p>
            Las cámaras 360° dedicadas (Insta360, Ricoh Theta…) hacen esto
            automáticamente con dos objetivos fisheye. Las apps para smartphone
            como Travvir logran el mismo resultado guiando la captura de
            múltiples imágenes secuenciales y realizando el stitching por
            software.
          </p>

          <h3>El formato equirectangular</h3>
          <p>
            Si descargas un archivo de foto 360° y lo abres en un visor
            estándar, la imagen aparece deformada y estirada, especialmente
            arriba y abajo. Esta distorsión es el resultado natural de proyectar
            una esfera sobre una superficie plana. Los bordes izquierdo y
            derecho representan el mismo punto: la vista directamente hacia
            atrás.
          </p>
        </section>

        <section>
          <h2>Cómo visualizar una foto 360°</h2>
          <p>
            Para ver correctamente una foto 360° necesitas un visualizador
            esférico que reproyecte el formato equirectangular en el interior de
            una esfera virtual. Te encontrarás en el centro mirando hacia el
            exterior, exactamente como si estuvieras en el lugar de la toma.
          </p>
          <ul>
            <li>
              <strong>Facebook</strong> — detecta y muestra fotos 360° de forma
              nativa en un visor interactivo.
            </li>
            <li>
              <strong>Google Street View</strong> — admite fotos 360° conformes
              con las especificaciones de Google.
            </li>
            <li>
              <strong>Travvir</strong> — incluye un visor web optimizado para
              tours virtuales.
            </li>
            <li>
              <strong>Visores VR</strong> — muestran las fotos 360° en
              experiencia completamente inmersiva.
            </li>
          </ul>
        </section>

        <section>
          <h2>Foto 360° vs foto panorámica: ¿cuál es la diferencia?</h2>
          <p>
            Una foto panorámica captura una franja horizontal ancha —a menudo
            180° o más— pero sigue siendo una imagen plana que no permite mirar
            arriba o abajo. Una foto 360° es una esfera completa: puedes mirar
            en todas las direcciones, incluidos el techo y el suelo. Esta
            cobertura esférica total es la que permite crear tours virtuales
            inmersivos.
          </p>
        </section>

        <section>
          <h2>Crear fotos 360° con Travvir</h2>
          <p>
            Con Travvir no necesitas una cámara 360° especial. Tu smartphone es
            suficiente: la IA guía la captura imagen a imagen, detecta las zonas
            que faltan y ensambla automáticamente el panorama esférico en 2 a 5
            minutos. El resultado es una foto 360° lista para integrarse en un
            tour virtual interactivo.
          </p>
        </section>

        <section>
          <h2>Preguntas frecuentes</h2>

          <h3>¿Qué resolución necesita una foto 360° de calidad?</h3>
          <p>
            Se recomienda un mínimo de 8 000 × 4 000 píxeles (32 megapíxeles)
            para una foto 360° nítida a nivel de visualización normal. Los
            smartphones actuales suelen superar esta resolución.
          </p>

          <h3>¿Se puede crear una foto 360° sin cámara especial?</h3>
          <p>
            Sí. Travvir ensambla varias fotos tomadas con tu smartphone para
            crear un panorama esférico completo sin equipo dedicado.
          </p>

          <h3>¿Una foto 360° es lo mismo que un tour virtual?</h3>
          <p>
            No exactamente. Una foto 360° es una imagen esférica única. Un tour
            virtual es un conjunto de varias fotos 360° conectadas por hotspots
            de navegación que permiten moverse de habitación en habitación de
            forma interactiva.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/tamano-imagen-tour-virtual">Tamaño y Resolución de Imágenes para Tours Virtuales 360° 2026</a></li>
          <li><a href="/blog/es/fotos-360-con-celular">Cómo Tomar Fotos 360° con tu Celular (Sin Cámara Especial) 2026</a></li>
          <li><a href="/blog/es/compartir-foto-360-redes-sociales">Cómo Compartir Fotos 360° en Redes Sociales 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-principiantes">Tour Virtual 360° para Principiantes: Crea tu 1er Tour desde el Móvil</a></li>
          <li><a href="/blog/es/panorama-vs-tour-virtual-360">Panorama Smartphone vs Tour Virtual 360°: ¿Cuál es la Diferencia? 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          <li><a href="/blog/es/corregir-fotos-360-borrosas">Fotos 360° Borrosas: 6 Causas y Soluciones Concretas 2026</a></li>
          <li><a href="/blog/es/tomar-fotos-360-movil">Cómo Tomar Fotos 360° con el Móvil: Técnica Completa 2026</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find((p) => p.slug === "que-es-foto-360");

useBlogSeo({
  title: "¿Qué es una Foto 360°? Fotografía 360 Explicada [2026] | Travvir",
  description:
    "Qué es una foto 360 (fotografía 360 / foto esférica): definición, equirectangular, cómo crearla con smartphone y usarla en un tour virtual.",
  keywords:
    "foto 360, fotos 360, fotografía 360, fotografia 360, foto esférica, qué es foto 360, equirectangular",
  slug: "que-es-foto-360",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Qué resolución necesita una foto 360° de calidad?",
      answer:
        "Se recomienda un mínimo de 8 000 × 4 000 píxeles. Los smartphones actuales suelen superar esta resolución.",
    },
    {
      question: "¿Se puede crear una foto 360° sin cámara especial?",
      answer:
        "Sí. Travvir ensambla varias fotos de smartphone para crear un panorama esférico completo sin equipo dedicado.",
    },
  ],
});

</script>
